"use client";

import {JSX, ReactNode} from "react";
import type {HTMLInputAutoCompleteAttribute} from "react";

import {useSnapshot} from "valtio";

import {useScrollIntoView} from "@/shared/hooks/dom/use-scroll-into-view";
import {useTranslation} from "@/shared/i18n";
import {type RangeInstance} from "@/shared/models/form/fields";
import {Typography} from "@/shared/ui/data-display/typography";

import {type FormFieldSize} from "./types";

import "./styles/range-field.scss";

interface RangeFieldProps {
    model: RangeInstance;
    label?: string;
    children?: ReactNode;
    labelPosition?: "top" | "left";
    minPlaceholder?: string;
    maxPlaceholder?: string;
    className?: string;
    size?: FormFieldSize;
    autoComplete?: HTMLInputAutoCompleteAttribute;
}

export function RangeField({
    model,
    label,
    children,
    labelPosition = "top",
    minPlaceholder = "Min",
    maxPlaceholder = "Max",
    className,
    size,
    autoComplete = "on",
}: RangeFieldProps): JSX.Element {
    const {t} = useTranslation("validation");
    const snapshot = useSnapshot(model);
    const ref = useScrollIntoView<HTMLDivElement>(model);

    const sizeClass = size ? `range-field--${size}` : "";
    const layoutClass = labelPosition === "left" ? "range-field--horizontal" : "";

    // Use children if provided, otherwise fall back to label string
    const labelContent = children || label;

    const handleMinChange = (value: string): void => {
        if (value === "") {
            model.setMinValue(null);
        } else {
            const numValue = Number(value);
            if (!isNaN(numValue)) {
                model.setMinValue(numValue);
            }
        }
    };

    const handleMaxChange = (value: string): void => {
        if (value === "") {
            model.setMaxValue(null);
        } else {
            const numValue = Number(value);
            if (!isNaN(numValue)) {
                model.setMaxValue(numValue);
            }
        }
    };

    return (
        <div ref={ref} className={`range-field ${sizeClass} ${layoutClass} ${className || ""}`}>
            {labelContent && (
                <Typography as="label" className="range-field__label" size={size === "sm" ? 12 : 14} weight={500} color="title">
                    {labelContent} {model.isRequired && <span className="range-field__required">*</span>}
                </Typography>
            )}

            <div className="range-field__content">
                <div className="range-field__inputs">
                    <input
                        type="number"
                        value={snapshot.minValue ?? ""}
                        onChange={(e) => handleMinChange(e.target.value)}
                        onBlur={() => model.onBlur()}
                        onFocus={() => model.onFocus()}
                        disabled={snapshot.disabled}
                        placeholder={minPlaceholder}
                        autoComplete={autoComplete}
                        className={`range-field__input ${snapshot.shouldShowError ? "has-error" : ""}`}
                    />

                    <span className="range-field__separator">-</span>

                    <input
                        type="number"
                        value={snapshot.maxValue ?? ""}
                        onChange={(e) => handleMaxChange(e.target.value)}
                        onBlur={() => model.onBlur()}
                        onFocus={() => model.onFocus()}
                        disabled={snapshot.disabled}
                        placeholder={maxPlaceholder}
                        autoComplete={autoComplete}
                        className={`range-field__input ${snapshot.shouldShowError ? "has-error" : ""}`}
                    />
                </div>

                {snapshot.shouldShowError && snapshot.error && (
                    <span className="range-field__error-text">
                        {t(snapshot.error.message as Parameters<typeof t>[0], snapshot.error.messageParams)}
                    </span>
                )}
            </div>
        </div>
    );
}
